import React, { createContext, ReactNode, useContext } from "react";
import communityImage from "@/assets/imgs/services/services_sunday.webp";
import youthImage from "@/assets/imgs/services/services_youth.webp";
import prayerImage from "@/assets/imgs/services/services_prayer.webp";
import fasting from "@/assets/imgs/events/2026-01_fasting.jpg";
import easter from "@/assets/imgs/events/easter-26.jpg";
import farewell from "@/assets/imgs/events/MFR-AFR-farewell-2026.jpg";
import T27 from "@/assets/imgs/events/T27.jpg";
import NWC from "@/assets/imgs/events/NWC26.png";
/* -------------------------------------------------------------------------- */
/*                                Event Type                                  */
/* -------------------------------------------------------------------------- */

// Who is this event for? If left out, it's treated as a church-wide event.
export type EventAudience = "church" | "youth" | "women" | "men";

export interface Event {
  id: string;
  title: string;

  startDate?: Date;
  endDate?: Date;

  isRecurring?: boolean;

  time: string;
  location: string;

  description: string;
  longDescription?: string;

  image: string;
  category: "Community" | "Youth" | "Worship" | "Special";

  audience?: EventAudience; // who the event is for (default: "church")
  featured?: boolean; // also show on church-wide lists, even if it's a ministry event
  registrationUrl?: string; // optional sign-up link
}

/* -------------------------------------------------------------------------- */
/*                             Context Definition                             */
/* -------------------------------------------------------------------------- */

interface EventsContextType {
  events: Event[]; // everything
  getEventById: (id: string) => Event | undefined;
  upcomingEvents: Event[]; // church-wide only
  pastEvents: Event[]; // church-wide only
  allUpcomingEvents: Event[]; // every audience, used by the events page
  getUpcomingEventsFor: (audience: EventAudience) => Event[];
  getPastEventsFor: (audience: EventAudience) => Event[];
}

const EventsContext = createContext<EventsContextType | undefined>(undefined);

/* -------------------------------------------------------------------------- */
/*                             Helper Functions                               */
/* -------------------------------------------------------------------------- */

const normalizeDate = (date?: Date): Date | null => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isToday = (date: Date): boolean => {
  const today = normalizeDate(new Date());
  const compare = normalizeDate(date);
  return !!today && !!compare && today.getTime() === compare.getTime();
};

// Church-wide = no audience set, audience is "church", or marked featured
const isChurchWide = (event: Event): boolean =>
  (event.audience ?? "church") === "church" || !!event.featured;

// Takes a list of events and returns the upcoming ones, soonest first
const getUpcoming = (list: Event[]): Event[] => {
  const today = normalizeDate(new Date())!;

  return list
    .filter((event) => {
      if (event.isRecurring) return true;

      const start = normalizeDate(event.startDate);
      const end = normalizeDate(event.endDate);

      if (!start) return false;
      if (end) return end >= today;

      return start >= today || isToday(start);
    })
    .sort((a, b) => {
      if (a.isRecurring) return -1;
      if (b.isRecurring) return 1;

      const aDate = normalizeDate(a.startDate);
      const bDate = normalizeDate(b.startDate);

      if (!aDate || !bDate) return 0;
      return aDate.getTime() - bDate.getTime();
    });
};

// Takes a list of events and returns the past ones, most recent first
const getPast = (list: Event[]): Event[] => {
  const today = normalizeDate(new Date())!;

  return list
    .filter((event) => {
      if (event.isRecurring) return false;

      const end = normalizeDate(event.endDate);
      const start = normalizeDate(event.startDate);

      if (end) return end < today;
      if (start) return start < today && !isToday(start);

      return false;
    })
    .sort((a, b) => {
      const aDate = normalizeDate(a.startDate);
      const bDate = normalizeDate(b.startDate);

      if (!aDate || !bDate) return 0;
      return bDate.getTime() - aDate.getTime();
    });
};

/* -------------------------------------------------------------------------- */
/*                                 All Events                                 */
/* -------------------------------------------------------------------------- */

const allEvents: Event[] = [
  {
    id: "14-days-fasting-prayer-2026",
    title: "14 Days of Fasting & Prayer 2026",
    startDate: new Date("2026-01-05"),
    endDate: new Date("2026-01-18"),
    time: "Daily",
    location: "All PIWC Churches",
    description:
      "A church-wide period of fasting and prayer focused on the Holy Spirit.",
    longDescription:
      "The entire Church will embark on a 14-day fasting and prayer journey, seeking the power and presence of the Holy Spirit.",
    image: fasting,
    category: "Special",
  },

  {
    id: "easter-convention-26",
    title: "Easter Convention 2026",
    startDate: new Date("2026-04-02"),
    endDate: new Date("2026-04-05"),
    time: "Various Times",
    location: "Shekinah Glory Temple & Zonal Centres",
    description:
      "Join us this Easter for a powerful time of worship, teaching, and spiritual renewal.",
    longDescription: `
      <p>Join us this Easter for a powerful time of worship, teaching, and spiritual renewal as we gather for our Easter Convention 2026. This convention is a special opportunity to reflect on the saving power of Jesus Christ and grow deeper in faith through impactful services and anointed ministrations.</p> <br>
      <p>This year's Easter Convention is based on the theme: <h2>ONLY JESUS CAN SAVE YOU</h2> taken from <strong> <span title="Neither is there salvation in any other: for there is none other name under heaven given among men, whereby we must be saved. (Acts 4:12)" ><u>Acts 4:12</u></span></strong>.</p><br><br>
      <h1>Easter Convention Schedule</h1>
      Thursday – Physical Service (Shekinah Glory Temple)<br>
      7:00 PM – 9:00 PM with Apostle Dr Lord Elorm Donkor<br><br>

      Friday – Good Friday Service (Shekinah Glory Temple)<br>
      11:00 AM – 1:00 PM<br><br>

      Saturday – Zonal Service<br>
      7:30 PM – 9:00 PM<br><br>

      Sunday – District Service<br>
      11:00 AM<br><br>

      <strong>Ministers:</strong><br>
      Pastor Michael Frimpong<br>
      Apostle Dr Lord Elorm Donkor<br>
      Prophet Emmanuel Danso<br>
      Rev Andrew Afropong
    `,
    image: easter,
    category: "Special",
  },

{
  id: "sunday-service",
  title: "Sunday Church Service",
  isRecurring: true,
  time: "10:00 AM - 12:30 PM",
  location: "Kildare Street, LE1 3FY",
  description:
    "Discover Sundays at Leicester PIWC and find out what to expect when you visit.",
 longDescription: `
  <p>Join us for a time of worship, biblical teaching, prayer, and fellowship as we grow together in faith. Whether you're visiting for the first time or returning, you're welcome here.</p>

  <div style="margin-top: 24px; padding: 18px 20px; background: #f4f7f9; border-radius: 6px;">
    <p style="margin: 0 0 8px; font-weight: 600; color: #00487C;">Planning to join us?</p>
    <p style="margin: 0 0 12px;">Find out what to expect and how to plan your visit.</p>
    <a href="/sundays" style="display: inline-block; color: #00487C; font-weight: 600; text-decoration: none;">Explore Sunday Services &rarr;</a>
  </div>
`,
  image: communityImage,
  category: "Worship",
},

  {
    id: "youth-meeting",
    title: "Weekly Youth Meeting",
    isRecurring: true,
    time: "7:30 PM - 9:00 PM",
    location: "Online via Teams",
    description: "Fellowship and growth for young people.",
    longDescription:
      "A space for young people to connect, have fun, and grow spiritually.",
    image: youthImage,
    category: "Youth",
    audience: "youth",
  },

  {
    id: "community-outreach-2025",
    title: "Community Outreach Day",
    startDate: new Date("2025-05-03"),
    time: "9:00 AM - 4:00 PM",
    location: "Leicester City Centre",
    description: "Serving the community through outreach.",
    longDescription:
      "Join us as we serve the city through practical acts of kindness and outreach.",
    image: youthImage,
    category: "Community",
  },

  {
    id: "mens-breakfast-2025",
    title: "Men's Breakfast",
    startDate: new Date("2025-05-10"),
    time: "8:00 AM - 10:00 AM",
    location: "Church Hall",
    description: "Monthly fellowship for men.",
    longDescription:
      "A time for men to connect, eat, and grow together spiritually.",
    image: prayerImage,
    category: "Community",
    audience: "men",
  },

  {
    id: "christmas-carols-2024",
    title: "Christmas Carol Service",
    startDate: new Date("2024-12-22"),
    time: "6:00 PM",
    location: "Main Sanctuary",
    description: "Celebrate Christmas with carols.",
    longDescription:
      "An evening of carols, music, and celebration of Christ’s birth.",
    image: communityImage,
    category: "Special",
  },

  {
    id: "farewell-service-26",
    title: "Farewell Service 2026",
    startDate: new Date("2026-08-09"),
    time: "11:00 AM",
    location: "Shekinah Glory Temple",
    description:
      "Join us as we honour Rev Michael & Mrs Abigail Frimpong and family for their dedicated service to Leicester District.",
    longDescription: `
    <p>Join the Leicester District of The Church of Pentecost UK as we come together for a special <strong>Farewell Service</strong> in honour of <strong>Rev Michael & Mrs Abigail Frimpong and family</strong>.</p><br>

    <p>This service is an opportunity for the district to express heartfelt appreciation and gratitude for their faithful leadership, sacrificial service, and commitment to God's work throughout their time in Leicester. Together, we will celebrate God's faithfulness, reflect on the impact of their ministry, and pray for His continued guidance and blessings as they begin the next chapter of their journey.</p><br>

    <h1>Service Details</h1>

    <strong>Date:</strong> Sunday, 9th August 2026<br>
    <strong>Time:</strong> 11:00 AM<br>
    <strong>Venue:</strong> Shekinah Glory Temple<br><br>

    <p>All members, families, and friends are warmly invited to attend this special occasion as we celebrate and give thanks for a remarkable season of ministry.</p>
  `,
    image: farewell,
    category: "Special",
  },

  /*{
    id: "dunamis",
    title: "Dunamis Prayer Night",
    startDate: new Date("2026-10-23"),
    time: "19:30 PM",
    location: "Shekinah Glory Temple",
    description:
      "Join us for for a night of fervent prayer, worship, and divine encounters. ",
    longDescription: `
    <p>
  <strong>Dunamis Prayer Night</strong> is a powerful evening of prayer, worship, and seeking the presence of God. Join us as we come together in faith, trusting in the power of the Holy Spirit to strengthen us, renew our hearts, and deepen our relationship with God. Come expectant, come in faith, and let us pray together for a fresh move of God in our lives and our community.
</p>
  `,
    image: farewell,
    category: "Special",
  },*/

  /* ----------------------------- Women's Ministry ----------------------------- */

  {
    id: "national-womens-conference",
    title: "National Women's Conference 2026",
    startDate: new Date("2026-10-28"),
    endDate: new Date("2026-10-31"),
    featured: true,
    time: "",
    location: "James McKeown Centre, Milton Keynes",
    description:
      "Join women from across the UK for four unforgettable days of worship, prayer, empowerment, fellowship, and spiritual growth.",
    longDescription: `
<p>The National Women's Conference 2026 brings women from across the UK together for four unforgettable days of worship, prayer, empowerment, fellowship and spiritual growth.</p><br>

<p>Come and be refreshed, inspired, and equipped for greater impact in God's kingdom. All attendees are encouraged to come with expectant hearts, ready to receive and grow in the presence of God.</p><br>

<h1>Event Details</h1>

<strong>Dates:</strong> Wednesday, 28th – Saturday, 31st October 2026<br>
<strong>Venue:</strong> James McKeown Centre, Milton Keynes<br>
<strong>Register:</strong> <a href="https://copukwomensconf26.zohobackstage.eu/COPUKWomensConference#/buyTickets/selectTickets?lang=en" target="_blank" rel="noopener noreferrer"><u>www.copuk.org</u></a>
  `,
    image: NWC,
    category: "Special",
    audience: "women",
    registrationUrl: "https://copukwomensconf26.zohobackstage.eu/COPUKWomensConference#/buyTickets/selectTickets?lang=en",
  },

  /* ------------------------------ Youth Ministry ------------------------------ */

  {
  id: "transformers-conference-2027",
  title: "Transformers Conference 2027",
  startDate: new Date("2027-07-27"),
  endDate: new Date("2027-07-30"),
  time: "",
  location: "First Direct Arena, Leeds",
  description:
    "A 4-day Christian conference of dynamic teaching, powerful worship and spirit-filled prayer, welcoming young Christians from all over the world.",
  longDescription: `
<p><strong>Transformers 2027</strong> is a four-day Christian conference for young people, hosted by the Church of Pentecost UK Youth &amp; PENSA Ministry. Expect dynamic teaching, powerful worship and spirit-filled prayer, with young Christians gathering from all over the world.</p><br>

<p>Transformers is the youth and young adults ministry of the Church of Pentecost UK, and it exists to transform a generation: to build unyielding faith in young people, equip them in the language of the Kingdom, and send them out as positive influences in society.</p><br>

<h1>Event Details</h1>

<strong>Dates:</strong> Tuesday, 27th – Friday, 30th July 2027<br>
<strong>Venue:</strong> First Direct Arena, Leeds<br>
<strong>Tickets:</strong> <a href="https://www.transformersconference.org/#/buyTickets/selectTickets?lang=en" target="_blank" rel="noopener noreferrer"><u>Get your tickets here</u></a>
  `,
  image: T27,
  category: "Youth",
  audience: "youth",
  registrationUrl:
    "https://www.transformersconference.org/#/buyTickets/selectTickets?lang=en",
},
];

/* -------------------------------------------------------------------------- */
/*                              Events Provider                               */
/* -------------------------------------------------------------------------- */

export const EventsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const churchEvents = allEvents.filter(isChurchWide);

  const upcomingEvents = getUpcoming(churchEvents);
  const pastEvents = getPast(churchEvents);

  // Every audience together, for the events page
  const allUpcomingEvents = getUpcoming(allEvents);

  // A missing audience counts as "church"
  const getUpcomingEventsFor = (audience: EventAudience) =>
    getUpcoming(allEvents.filter((e) => (e.audience ?? "church") === audience));

  const getPastEventsFor = (audience: EventAudience) =>
    getPast(allEvents.filter((e) => (e.audience ?? "church") === audience));

  const getEventById = (id: string) =>
    allEvents.find((event) => event.id === id);

  return (
    <EventsContext.Provider
      value={{
        events: allEvents,
        getEventById,
        upcomingEvents,
        pastEvents,
        allUpcomingEvents,
        getUpcomingEventsFor,
        getPastEventsFor,
      }}
    >
      {children}
    </EventsContext.Provider>
  );
};

/* -------------------------------------------------------------------------- */
/*                                Hook Export                                 */
/* -------------------------------------------------------------------------- */

export const useEvents = () => {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error("useEvents must be used within an EventsProvider");
  }
  return context;
};